import { supabase } from './supabase';
import type { Tier } from './access';

/* Razorpay checkout for subscription upgrades. The secret never reaches the
   client: subscription-create-order makes the order server-side and
   subscription-verify validates the signature and auto-activates the plan. */

const SDK_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  order_id: string;
  handler: (r: RazorpayResponse) => void;
  prefill?: { name?: string; email?: string; contact?: string };
  theme?: { color?: string };
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance { open: () => void; }

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

/** True when a publishable key is configured at build time. */
export function razorpayConfigured(): boolean {
  return !!(import.meta.env.VITE_RAZORPAY_KEY_ID as string | undefined);
}

let sdkPromise: Promise<boolean> | null = null;

/** Inject the Razorpay Checkout script once; resolves true when ready. */
export function loadRazorpaySdk(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<boolean>((resolve) => {
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => { sdkPromise = null; resolve(false); };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

export interface PayForPlanParams {
  plan: Tier;
  /** Plan price in rupees (e.g. 8999). Converted to paise for Razorpay. */
  amount: number;
  restaurantId: string;
  onSuccess: () => void | Promise<void>;
}

/** Full upgrade flow: create order → open checkout → verify + auto-activate. */
export async function payForPlan({ plan, amount, restaurantId, onSuccess }: PayForPlanParams): Promise<void> {
  if (!razorpayConfigured()) throw new Error("Online payment isn’t set up yet.");

  const amountPaise = Math.round(amount * 100);

  // 1) Create the order server-side.
  const { data: order, error: orderErr } = await supabase.functions.invoke('subscription-create-order', {
    body: { amount: amountPaise, plan, restaurantId },
  });
  if (orderErr) throw new Error(orderErr.message || 'Could not start the payment.');
  if (!order?.orderId || !order?.keyId) {
    throw new Error(order?.error || 'Online payment isn’t set up yet.');
  }

  // 2) Load the checkout SDK.
  const ready = await loadRazorpaySdk();
  if (!ready || !window.Razorpay) throw new Error('Could not load the payment window. Check your connection.');

  // 3) Open checkout and, on success, verify + activate.
  await new Promise<void>((resolve, reject) => {
    const rzp = new window.Razorpay!({
      key: order.keyId,
      amount: amountPaise,
      currency: 'INR',
      name: 'EzyDine',
      description: `EzyDine ${plan} plan`,
      order_id: order.orderId,
      theme: { color: '#0F766E' },
      modal: { ondismiss: () => reject(new Error('Payment cancelled.')) },
      handler: async (resp) => {
        try {
          const { data: verify, error: vErr } = await supabase.functions.invoke('subscription-verify', {
            body: {
              orderId: resp.razorpay_order_id,
              paymentId: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
              plan,
              amount: amountPaise,
              restaurantId,
            },
          });
          if (vErr) throw new Error(vErr.message || 'Could not verify the payment.');
          if (!verify?.activated) throw new Error(verify?.error || 'Payment could not be verified.');
          await onSuccess();
          resolve();
        } catch (e) {
          reject(e instanceof Error ? e : new Error('Payment verification failed.'));
        }
      },
    });
    rzp.open();
  });
}
